import { LinearProgress } from '@mui/joy';

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import Breadcrumbs from '@/components/Breadcrumbs';
import {
  SessionsCustomCodeEvaluatorCrudPane,
  SessionsEvaluatorRuleCrudPane,
} from '@/components/EvaluatorCrudPane/SessionsEvaluatorCrudPane';
import { ThreadEvaluatorCrudPane } from '@/components/EvaluatorCrudPane/ThreadEvaluatorCrudPane';
import { EvaluatorStoreProvider } from '@/components/EvaluatorCrudPane/store/EvaluatorStoreProvider';
import { useIsPromptLoading } from '@/hooks/useIsPromptLoading';
import { useRunRules } from '@/hooks/useSwr';

import { useEvaluatorStore } from '../../components/EvaluatorCrudPane/store/evaluatorStore';
import { SessionEvaluatorDetailsPage } from './SessionEvaluatorDetailsPage';

export const SessionEvaluatorDetails = () => {
  return (
    <EvaluatorStoreProvider storeType="detail">
      <SessionEvaluatorDetailsInner />
    </EvaluatorStoreProvider>
  );
};

const SessionEvaluatorDetailsInner = () => {
  const { sessionId, evaluatorId } = useParams();
  const [llmAsJudgePaneOpen, setLlmAsJudgePaneOpen] = useState(false);
  const [customCodePaneOpen, setCustomCodePaneOpen] = useState(false);
  const [threadPaneOpen, setThreadPaneOpen] = useState(false);
  const importEvaluator = useEvaluatorStore((state) => state.importEvaluator);
  const reset = useEvaluatorStore((state) => state.reset);

  const {
    data: rules,
    isLoading,
    mutate,
  } = useRunRules(evaluatorId ? { id: [evaluatorId] } : undefined, {
    keepPreviousData: false,
  });
  const evaluator = rules?.find((r) => r.id === evaluatorId);

  useEffect(() => {
    if (rules?.[0]?.evaluators?.[0]?.structured) {
      // Data is available, import it into the store
      importEvaluator(
        rules[0].evaluators[0].structured,
        'dataset',
        null,
        rules[0].evaluator_version
      );
    }

    // Cleanup: reset store when component unmounts or evaluatorId changes
    return () => {
      reset();
    };
  }, [rules, reset, importEvaluator]);

  // If it's a code evaluator, we don't need to load the prompt
  const isLoadingPrompt =
    useIsPromptLoading(
      evaluator?.group_by === 'thread_id' ? 'thread-eval' : 'session-eval'
    ) && !evaluator?.code_evaluators?.length;

  const pageContent = useMemo(() => {
    if (isLoading || isLoadingPrompt) {
      return (
        <div className="flex flex-col">
          <LinearProgress />
        </div>
      );
    }
    if (!evaluator) {
      return <div className="p-6">Evaluator not found</div>;
    }
    return (
      <SessionEvaluatorDetailsPage
        evaluator={evaluator}
        setEditModalOpen={(isOpen) => {
          if (evaluator?.evaluators?.[0]?.structured) {
            if (evaluator.group_by === 'thread_id') {
              setThreadPaneOpen(isOpen);
            } else {
              setLlmAsJudgePaneOpen(isOpen);
            }
          } else {
            setCustomCodePaneOpen(isOpen);
          }
        }}
        filter={evaluator?.filter}
        traceFilter={evaluator?.trace_filter}
        treeFilter={evaluator?.tree_filter}
        samplingRate={evaluator?.sampling_rate}
      />
    );
  }, [isLoading, isLoadingPrompt, evaluator]);

  return (
    <div className="flex min-h-screen flex-1 flex-col bg-primary">
      <div className="bg-primary px-4 pt-3">
        <Breadcrumbs />
      </div>

      {pageContent}

      {/* Edit Evaluator Modal */}
      <EvaluatorStoreProvider storeType="crud">
        <SessionsEvaluatorRuleCrudPane
          initialData={evaluator}
          evaluator={evaluator?.evaluators?.[0]?.structured}
          sessionId={sessionId!}
          open={llmAsJudgePaneOpen}
          setOpen={(isOpen) => {
            if (!isOpen && llmAsJudgePaneOpen) {
              // Modal is being closed, reset store to original state
              if (evaluator?.evaluators?.[0]?.structured) {
                importEvaluator(
                  evaluator.evaluators[0].structured,
                  'session',
                  null,
                  evaluator.evaluator_version
                );
              }
            }
            setLlmAsJudgePaneOpen(isOpen);
          }}
          onSave={() => {
            // This will be handled by the SessionsEvaluatorRuleCrudPane
            mutate(undefined); // Refresh the evaluator data
            setLlmAsJudgePaneOpen(false);
          }}
          isSaving={false}
          runTableFilters={{
            filter: evaluator?.filter ?? 'eq(is_root, true)',
            traceFilter: evaluator?.trace_filter,
            treeFilter: evaluator?.tree_filter,
          }}
          setRunTableFilters={() => {}}
        />
        <SessionsCustomCodeEvaluatorCrudPane
          initialData={evaluator}
          sessionId={sessionId!}
          open={customCodePaneOpen}
          setOpen={(isOpen) => {
            setCustomCodePaneOpen(isOpen);
          }}
          runTableFilters={{
            filter: evaluator?.filter ?? 'eq(is_root, true)',
            traceFilter: evaluator?.trace_filter,
            treeFilter: evaluator?.tree_filter,
          }}
          setRunTableFilters={() => {}}
        />
        <ThreadEvaluatorCrudPane
          initialData={evaluator}
          sessionId={sessionId!}
          open={threadPaneOpen}
          setOpen={(isOpen) => {
            setThreadPaneOpen(isOpen);
          }}
          onSave={() => {
            mutate(undefined); // Refresh the evaluator data
            setThreadPaneOpen(false);
          }}
        />
      </EvaluatorStoreProvider>
    </div>
  );
};
